import {
  ReactElement, memo, useCallback, useState, useEffect,
} from 'react';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import { useDispatcher } from 'store/utils/redux/hooks';
import * as invitationStore from 'store/nodes/invitation';
import * as cardStore from 'store/nodes/card';

import Button from 'components/Button';
import TextInput from 'components/TextInput';
import NumberInput from 'components/NumberInput';
import Box from '@mui/joy/Box';

const Info = (): ReactElement => {
  const classes = useStyles();
  const dispatcher = useDispatcher();

  const isShareableLink = useSelector(cardStore.selectors.isShareableLink);
  const eventData = useSelector(invitationStore.selectors.getEvent);

  const isLoadingInfo = useSelector(invitationStore.selectors.isLoading('info'));

  const [name, setName] = useState<string>('');
  const [contact, setContact] = useState<string>('');
  const [numberOfTotalAttendees, setNumberOfTotalAttendees] = useState<number | undefined>(1);
  const [numberOfAdultAttendees, setNumberOfAdultAttendees] = useState<number | undefined>(1);
  const [numberOfChildrenAttendees, setNumberOfChildrenAttendees] = useState<number | undefined>(0);
  const [isFieldValid, setFieldValid] = useState<Record<'name' | 'contact', boolean>>({
    name: true,
    contact: true,
  });

  useEffect(() => {
    const savedName = localStorage.getItem('nameSavedInvitation');
    const savedContact = localStorage.getItem('contactSavedInvitation');
    const savedNumberOfTotalAttendees = localStorage.getItem('numberOfTotalAttendeesSavedInvitation');
    const savedNumberOfAdultAttendees = localStorage.getItem('numberOfAdultAttendeesSavedInvitation');
    const savedNumberOfTotalAttendeesKids = localStorage.getItem('numberOfChildrenAttendeesSavedInvitation');
    if (savedName) {
      setName(savedName);
    }
    if (savedContact) {
      setContact(savedContact);
    }
    if (savedNumberOfTotalAttendees) {
      setNumberOfTotalAttendees(Number(savedNumberOfTotalAttendees));
    }
    if (savedNumberOfAdultAttendees) {
      setNumberOfAdultAttendees(Number(savedNumberOfAdultAttendees));
    }
    if (savedNumberOfTotalAttendeesKids) {
      setNumberOfChildrenAttendees(Number(savedNumberOfTotalAttendeesKids));
    }
  }, []);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    setFieldValid({
      ...isFieldValid,
      name: true,
    });
  }, [isFieldValid]);

  const handleContactChange = useCallback((value: string) => {
    setContact(value);
    setFieldValid({
      ...isFieldValid,
      contact: true,
    });
  }, [isFieldValid]);

  const handleNumberOfTotalAttendees = useCallback((value: number | undefined) => {
    setNumberOfTotalAttendees(value);
  }, [numberOfTotalAttendees]);

  const handleNumberOfAdultAttendees = useCallback((value: number | undefined) => {
    setNumberOfAdultAttendees(value);
  }, []);

  const handleNumberOfChildrenAttendees = useCallback((value: number | undefined) => {
    setNumberOfChildrenAttendees(value);
  }, []);

  const handleBack = useCallback(() => {
    dispatcher.invitation.setStep('attending');
  }, []);

  const validate = useCallback(() => {
    if (!isShareableLink) {
      return true;
    }
    const newErrorField = { ...isFieldValid };
    if (!name) {
      newErrorField.name = false;
    }
    if (!contact) {
      newErrorField.contact = false;
    }
    setFieldValid(newErrorField);
    return Object.values(newErrorField).reduce((result, value) => {
      if (!result) {
        return false;
      }
      return value;
    }, true);
  }, [name, contact, isFieldValid, isShareableLink]);

  const handleNext = useCallback(() => {
    const isValid = validate();
    if (!isValid) {
      return;
    }
    if (eventData?.shouldExcludeChildren === true) {
      dispatcher.invitation.sendInfo({
        type: 'Total', numberOfTotalAttendees: numberOfTotalAttendees || 1, name, contact,
      });
    } else {
      dispatcher.invitation.sendInfo({
        type: 'AdultChild', numberOfAdultAttendees: numberOfAdultAttendees || 1, numberOfChildrenAttendees: numberOfChildrenAttendees || 0, name, contact,
      });
    }
    localStorage.setItem('nameSavedInvitation', name);
    localStorage.setItem('contactSavedInvitation', contact);
    localStorage.setItem('numberOfTotalAttendeesSavedInvitation', numberOfTotalAttendees?.toString() || '1');
    localStorage.setItem('numberOfAdultAttendeesSavedInvitation', numberOfAdultAttendees?.toString() || '1');
    localStorage.setItem('numberOfChildrenAttendeesSavedInvitation', numberOfChildrenAttendees?.toString() || '0');
  }, [name, contact, validate, numberOfTotalAttendees, numberOfAdultAttendees, numberOfChildrenAttendees]);

  return (
    <div className={classes.container}>
      <div className={classes.title}>{eventData?.title}</div>
      {eventData?.startTime && (
        <div className={classes.blockDate}>{DateTime.fromISO(eventData?.startTime, { locale: 'en' }).toLocaleString(DateTime.DATETIME_MED)}</div>
      )}
      {isShareableLink && (
        <>
          <TextInput
            label="Your Name:"
            value={name}
            onChange={handleNameChange}
            className={classes.input}
            hasError={!isFieldValid.name}
            errorText="Name is required"
            disabled={isLoadingInfo}
          />
          <TextInput
            label="Phone or Email:"
            value={contact}
            onChange={handleContactChange}
            className={classes.input}
            hasError={!isFieldValid.contact}
            errorText="Phone or Email is required"
            disabled={isLoadingInfo}
          />
        </>
      )}
      {eventData?.shouldExcludeChildren === true && (
        <NumberInput
          label="Total Guests (including you):"
          value={numberOfTotalAttendees}
          className={classes.input}
          onChange={handleNumberOfTotalAttendees}
          max={10}
          min={1}
          isDisabled={isLoadingInfo}
        />
      )}
      {eventData?.shouldExcludeChildren === false && (
        <Box className={classes.input} display="flex" flexDirection="row" gap={2} alignItems="flex-end">
          <NumberInput
            label="Total Adults (including you):"
            value={numberOfAdultAttendees}
            onChange={handleNumberOfAdultAttendees}
            max={10}
            min={1}
            isDisabled={isLoadingInfo}
          />
          <NumberInput
            label="Total Children:"
            value={numberOfChildrenAttendees}
            onChange={handleNumberOfChildrenAttendees}
            max={10}
            min={0}
            isDisabled={isLoadingInfo}
          />
        </Box>
      )}
      <div className={classes.buttons}>
        <Button
          width="full"
          height={44}
          htmlColor="#F2F2F2"
          htmlTextColor="#3F3F3F"
          isDisabled={isLoadingInfo}
          onPress={handleBack}
        >
          Back
        </Button>
        <Button
          width="full"
          height={44}
          htmlColor="#20B2A4"
          isLoading={isLoadingInfo}
          isDisabled={isLoadingInfo}
          onPress={handleNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  title: {
    fontWeight: '700',
    fontSize: '2.2rem',
    lineHeight: 1.3,
    textAlign: 'left',
    color: '#454545',
  },
  input: {
    '&': {
      marginTop: '2.8rem',
    },
    '& + &': {
      marginTop: '0.6rem',
    },
  },
  blockDate: {
    marginTop: '1rem',
    fontSize: '1.5rem',
    color: '#3F3F3F',
  },
  buttons: {
    '&': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: '2.4rem',
    },
    '& > button': {
      marginRight: '1.6rem',
    },
    '& > button:last-child': {
      marginRight: 0,
    },
  },
});

export default memo(Info);
